import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { useAdminApi } from "./useAdminApi";
import { ImageField } from "./ImageField";
import { errMsg } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

const GROUPS = [
  ["Campaign", [["campaign_title", "Campaign title"], ["campaign_goal", "Goal amount ($)", "number"], ["offline_raised", "Offline / cash / check gifts ($) — added to the online total", "number"], ["campaign_years", "Years sustained", "number"], ["events_per_year", "Events per year", "number"], ["campaign_blurb", "Campaign description", "textarea"]]],
  ["Homepage", [["hero_eyebrow", "Hero eyebrow"], ["hero_title", "Hero headline"], ["hero_subtitle", "Hero subtitle", "textarea"], ["mission", "Mission", "textarea"], ["story", "Community story", "textarea"], ["ways_video_url", "\"Ways in\" video (next to the numbered list)", "video"]]],
  ["Schedule block (tiles come from Events)", [["schedule_label", "Season label (white box)"], ["schedule_title", "Big title"], ["schedule_subtitle", "Subtitle"], ["schedule_note", "Note under the schedule", "textarea"]]],
  ["Poem of the month (homepage)", [["potm_enabled", "Show this section on the homepage", "checkbox"], ["potm_month", "Month (e.g. November 2026)"], ["potm_title", "Poem title"], ["potm_text", "Poem (line breaks are kept)", "poem"], ["potm_poet", "Poet's name"], ["potm_poet_note", "Under the name (e.g. Poetry Club member)"], ["potm_image", "Picture next to the poem (optional)", "image"]]],
  ["Poetry Club", [["club_meeting_info", "Registration intro", "textarea"], ["club_culture", "Culture", "textarea"], ["club_history", "History", "textarea"]]],
  ["Def Street", [["defstreet_intro", "Festival intro", "textarea"], ["defstreet_date", "Festival date"], ["defstreet_location", "Festival location"]]],
  ["Community & Impact", [["community_intro", "Community intro", "textarea"], ["volunteer_intro", "Volunteer intro", "textarea"], ["impact_intro", "Impact intro", "textarea"], ["team_heading", "Impact page: team section heading"], ["team_intro", "Impact page: team section intro", "textarea"]]],
  ["Links & contact", [["series_url", "MAS Series link"], ["contact_email", "Contact email"], ["address", "Address (footer)"], ["instagram_url", "Instagram URL"]]],
];

const ListEditor = ({ title, items, setItems, fields, blank, testid }) => (
  <div className="bg-white border-2 border-mas-ink p-5 space-y-3" data-testid={testid}>
    <p className="eyebrow">{title}</p>
    {items.map((it, i) => (
      <div key={i} className="flex gap-2 items-start">
        {fields.map(([k, ph, type]) => (
          <input key={k} type={type || "text"} className={`field ${type === "number" ? "!w-24" : ""}`} placeholder={ph} value={it[k]}
            onChange={(e) => setItems(items.map((x, j) => (j === i ? { ...x, [k]: type === "number" ? Number(e.target.value) : e.target.value } : x)))} data-testid={`${testid}-${i}-${k}`} />
        ))}
        <button type="button" onClick={() => setItems(items.filter((_, j) => j !== i))} className="p-2.5 text-mas-terra" data-testid={`${testid}-${i}-remove`}><Trash2 className="w-4 h-4" /></button>
      </div>
    ))}
    <button type="button" onClick={() => setItems([...items, blank])} className="btn-ink !py-1.5 !text-xs" data-testid={`${testid}-add`}><Plus className="w-3.5 h-3.5" />Add</button>
  </div>
);

export default function AdminSettings() {
  const api = useAdminApi();
  const site = useSite();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (site?.settings && !s) setS(site.settings);
  }, [site, s]);
  if (!s) return <div className="h-40 animate-pulse paper-card" />;
  const set = (k, v) => setS({ ...s, [k]: v });
  const total = (s.impact_items || []).reduce((a, b) => a + Number(b.percent || 0), 0);

  const save = async () => {
    setBusy(true);
    try {
      await api("put", "/admin/settings", s);
      await site.refresh();
      toast.success("Saved — the website is updated");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8 max-w-4xl" data-testid="admin-settings">
      {GROUPS.map(([g, fields]) => (
        <div key={g} className="bg-white border-2 border-mas-ink p-5 space-y-4">
          <p className="eyebrow">{g}</p>
          {fields.map(([k, label, type]) => (
            <label key={k} className="block">
              <span className="text-xs font-semibold text-mas-mute">{label}</span>
              {type === "checkbox" ? (
                <input type="checkbox" className="ml-3 w-5 h-5 align-middle accent-[#ED773C]" checked={!!s[k]} onChange={(e) => set(k, e.target.checked)} data-testid={`admin-setting-${k}`} />
              ) : type === "poem" ? (
                <textarea rows={12} className="field mt-1 font-mono" value={s[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`admin-setting-${k}`} />
              ) : type === "image" ? (
                <div className="mt-1"><ImageField value={s[k] || ""} onChange={(v) => set(k, v)} testid={`admin-setting-${k}`} /></div>
              ) : type === "video" ? (
                <div className="mt-1"><ImageField accept="video/*" value={s[k] || ""} onChange={(v) => set(k, v)} testid={`admin-setting-${k}`} /></div>
              ) : type === "textarea" ? (
                <textarea rows={3} className="field mt-1" value={s[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`admin-setting-${k}`} />
              ) : (
                <input type={type || "text"} className="field mt-1" value={s[k] ?? ""} onChange={(e) => set(k, type === "number" ? Number(e.target.value) : e.target.value)} data-testid={k === "campaign_goal" ? "admin-edit-goal-input" : `admin-setting-${k}`} />
              )}
            </label>
          ))}
        </div>
      ))}
      <ListEditor title={`Where funds go (total ${total}%)`} items={s.impact_items || []} setItems={(v) => set("impact_items", v)} testid="admin-impact-items"
        fields={[["label", "Label"], ["percent", "%", "number"], ["description", "Description"]]} blank={{ label: "", percent: 0, description: "" }} />
      <ListEditor title="What contributions make possible" items={s.contribution_levels || []} setItems={(v) => set("contribution_levels", v)} testid="admin-contribution-levels"
        fields={[["amount", "$", "number"], ["outcome", "What it funds"]]} blank={{ amount: 50, outcome: "" }} />
      <div className="bg-white border-2 border-mas-ink p-5 space-y-4">
        <p className="eyebrow">Page images</p>
        {Object.keys(s.images || {}).map((k) => (
          <div key={k}><p className="text-xs font-semibold text-mas-mute mb-1 capitalize">{k}</p>
            <ImageField value={s.images[k]} onChange={(v) => set("images", { ...s.images, [k]: v })} testid={`admin-image-${k}`} /></div>
        ))}
      </div>
      <div className="sticky bottom-4">
        <button onClick={save} disabled={busy} className="btn-terra shadow-lg" data-testid="admin-settings-save-button">{busy ? "Saving..." : "Save changes"}</button>
      </div>
    </div>
  );
}
